import React, { Fragment } from 'react';
import styled from 'styled-components';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import useTranslation from 'hooks/use-translation';
import useDispensaryFlag from 'shared/hooks/use-dispensary-flag';

import { formatAddress } from 'shared/helpers/address';
import { AnchorButton } from 'components/core/button';
import { Typography } from 'src/typography';
import OrderingHours from './store-ordering-hours';
import { OrderingHoursV2 } from './store-ordering-hours-v2';

const OrderingHoursDescription = ({ t }) => (
  <Description>
    {t('dispensary.storeInfo.description', 'Hours below are online ordering hours and may not reflect store hours.')}
  </Description>
);

export default function StoreContent({ dispensary }) {
  const { location } = dispensary;
  const { t } = useTranslation();
  const showV2HoursInfo = useDispensaryFlag(`rollout.use-updated-ordering-configurations`, dispensary.id);

  const addressLine = formatAddress(dispensary.location);
  const googleMapsDirectionsUrl = `https://www.google.com/maps?q=${encodeURIComponent(addressLine)}`;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down(`sm`));
  const hideLocation = !!dispensary?.storeSettings.hideAddressFromDutchieMain;
  const hideHours = !!dispensary?.featureFlags.hideStoreHours;

  return (
    <SectionContent hideLocation={hideLocation}>
      <TwoColumn isMobile={isMobile}>
        {!hideHours && (
          <OrderingHoursContainer isMobile={isMobile}>
            <SectionTitle size='small' tag='h3'>
              {(props) => props.t('dispensary.storeInfo.orderingHours', 'Ordering Hours')}
            </SectionTitle>
            {showV2HoursInfo && (
              <OrderingHoursV2 dispensary={dispensary}>
                <OrderingHoursDescription t={t} />
              </OrderingHoursV2>
            )}
            {!showV2HoursInfo && (
              <Fragment>
                <OrderingHoursDescription t={t} />
                <OrderingHours dispensary={dispensary} />
              </Fragment>
            )}
          </OrderingHoursContainer>
        )}
        {!hideLocation && (
          <AddressContainer>
            <Title size='small' tag='h2'>
              {(props) => props.t('common.location', 'Location')}
            </Title>
            <AddressFormat>
              {location.ln1 && location.ln1} {location.ln2 && location.ln2} {location.city}, {location.state}
            </AddressFormat>

            <Directions
              href={googleMapsDirectionsUrl}
              target='_blank'
              rel='noopener noreferrer'
              variant='outline'
              size='large'
            >
              {t('dispensary.storeInfo.directions', 'Directions')}
            </Directions>
          </AddressContainer>
        )}
      </TwoColumn>
    </SectionContent>
  );
}

const AddressContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  display: none;

  @media (min-width: 960px) {
    display: flex;
    justify-content: flex-start;
    flex-direction: column;
    flex-basis: 40%;
  }
`;

const AddressFormat = styled.div`
  text-align: left;
  font-size: 13px;
  line-height: 145%;
  flex-basis: 100%;
`;

const Directions = styled(AnchorButton)`
  padding: 0;
  border: 0;
  background: transparent;
  text-transform: capitalize;
  font-size: 13px;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.primaryBlue};

  @media (min-width: 960px) {
    font-size: 12px;
    line-height: 12px;
    text-transform: uppercase;
    border: 2px solid ${({ theme }) => theme.colors.primaryBlue};
    border-radius: 27px;
    padding: 8px 15px;
    font-weight: bold;
    width: fit-content;
    margin-top: 13px;
  }
`;

const Title = styled(Typography.Heading)`
  flex-basis: 100%;
  margin-bottom: 10px;
`;

const TwoColumn = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primaryBlack};
  justify-content: center;

  @media (min-width: 960px) {
    justify-content: space-between;
  }
`;

const OrderingHoursContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-basis: ${({ isMobile }) => (isMobile ? `auto` : `60%`)};
  max-width: ${({ isMobile }) => (isMobile ? `315px` : `unset`)};
`;

const SectionContent = styled.div`
  margin-top: 50px;
  width: 100%;

  @media (min-width: 960px) {
    margin-top: ${({ hideLocation }) => (hideLocation ? `0px` : `40px`)};
  }
`;

const Description = styled.p`
  font-size: 12px;
  line-height: 165%;
  color: ${({ theme }) => theme.colors.grey[35]};
  margin-bottom: 25px;
`;

const SectionTitle = styled(Typography.Heading)`
  color: ${({ theme }) => theme.colors.primaryBlack};
  margin-bottom: 10px;
  margin-top: 0;
`;
